import { FC, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router";
import cn from "classnames";

import { Button, Icon, UrlQueryParams, useGetURLQuery } from "../../index";
import { BackButtonProps } from "../index";

import { useAlert } from "capitalrx-components-2.0/lib/general";

import styles from "./BackButton.module.scss";

interface UrlParams {
  client?: string;
}

const BackButton: FC<BackButtonProps> = ({ classNames, children, tier = "tertiary", text = "Back" }: BackButtonProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const showAlert = useAlert();

  const { client }: UrlParams = useParams();

  const failureHandler = useCallback(
    (err: string | Error): void => {
      showAlert({ type: "error", message: typeof err === "string" ? err : err.message, style: "banner" });
    },
    [showAlert],
  );

  const urlParams = useGetURLQuery<UrlQueryParams>(failureHandler);

  const previousPage = () => {
    const currentPage = location;

    const searchParams = new URLSearchParams();

    for (const key of Object.getOwnPropertyNames(urlParams)) {
      searchParams.append(key, urlParams[key]);
    }

    const encodedSearchParams = encodeURIComponent(searchParams.toString());

    if (currentPage.pathname === "/find-best-price/drug-details") {
      navigate({
        pathname: urlParams.group_number ? `/${urlParams.group_number}` : "/",
      });
    }
    if (currentPage.pathname === "/find-best-price/price-list") {
      navigate({
        pathname: "/find-best-price/drug-details",
        search: `?${encodedSearchParams}`,
      });
    }
    if (currentPage.pathname === "/find-best-price/pharmacy-details") {
      navigate({
        pathname: "/find-best-price/price-list",
        search: `?${encodedSearchParams}`,
      });
    }

    // whitelabeled
    if (currentPage.pathname === `/client/${client}/find-best-price/drug-details`) {
      navigate({
        pathname: `/client/${client}`,
      });
    }
    if (currentPage.pathname === `/client/${client}/find-best-price/price-list`) {
      navigate({
        pathname: `/client/${client}/find-best-price/drug-details`,
        search: `?${encodedSearchParams}`,
      });
    }
    if (currentPage.pathname === `/client/${client}/find-best-price/pharmacy-details`) {
      navigate({
        pathname: `/client/${client}/find-best-price/price-list`,
        search: `?${encodedSearchParams}`,
      });
    }
  };

  const backClasses = cn(styles["BackButton"], classNames);

  return (
    <Button tier={tier} size="small" className={backClasses} onClick={previousPage} testId="advantage-back-button">
      <>
        <Icon
          className={styles.BackButtonDesktopIcon}
          color={tier === "primary" ? "white" : "turquoise"}
          name="arrow-left"
          ariaHidden="true"
          testId="advantage-back-button-desktop-icon"
        />
        {text}
        <span className={styles.BackButtonMobileIcon}>
          <Icon
            name="carat-left"
            ariaHidden="true"
            color={tier === "primary" ? "white" : "turquoise"}
            testId="advantage-back-button-mobile-icon"
          />
          {children}
        </span>
      </>
    </Button>
  );
};

export default BackButton;
